import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Outlet, matchPath, useLocation } from 'react-router-dom';
import './portalResponsive.css';

import { useParentPageTracking } from '@/lib/parentPageAnalytics';
import { IncidentBanner } from '@/components/IncidentBanner';
import { PortalAnalyticsConsentBanner } from '@/components/PortalAnalyticsConsentBanner';
import { useProductInteractionTracking, useProductOpenTracking } from '@/lib/productOpenAnalytics';

import { PortalMobileHeader } from './PortalMobileHeader';
import { PortalMobileNav } from './PortalMobileNav';
import { usePortalPendingCount } from './usePortalPendingCount';

// IMMERSIVE routes take over the viewport: no header, dock, consent footer or
// padded frame. Family Apps render their own slim top bar
// (photo-nutrition-p1-integration-prd.md §3.2). Same idea as LearnLayout.
const IMMERSIVE_ROUTE_PATTERNS = ['/portal/apps/:pluginId'] as const;

function isImmersivePortalPath(pathname: string): boolean {
  return IMMERSIVE_ROUTE_PATTERNS.some((path) => matchPath({ path, end: true }, pathname));
}

export function PortalLayout() {
  const pendingCount = usePortalPendingCount();
  const location = useLocation();
  const [isStarsFullscreenOpen, setIsStarsFullscreenOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const isFullscreenMenuOpen = isStarsFullscreenOpen;
  useParentPageTracking();
  useProductOpenTracking('user');
  useProductInteractionTracking('user');

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    if (isFullscreenMenuOpen) main.setAttribute('inert', '');
    else main.removeAttribute('inert');
  }, [isFullscreenMenuOpen]);

  useLayoutEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
    const frame = window.requestAnimationFrame(() => {
      if (mainRef.current) mainRef.current.scrollTop = 0;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);

  if (isImmersivePortalPath(location.pathname)) {
    return (
      <div
        className="portal-shell fixed inset-0 flex h-dvh min-h-0 flex-col overflow-hidden bg-canvas"
        data-portal-shell="true"
        data-portal-immersive="true"
        data-testid="portal-layout"
      >
        <IncidentBanner />
        <main className="min-h-0 min-w-0 flex-1 overflow-hidden" data-testid="portal-immersive-region">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div
      className="portal-shell fixed inset-0 flex h-dvh min-h-0 overflow-hidden bg-canvas"
      data-portal-shell="true"
      data-testid="portal-layout"
    >
      <main
        ref={mainRef}
        aria-hidden={isFullscreenMenuOpen || undefined}
        className={`min-h-0 min-w-0 flex-1 overflow-x-hidden scroll-pb-[calc(8rem+env(safe-area-inset-bottom))] overscroll-y-contain pb-[calc(7.5rem+env(safe-area-inset-bottom))] ${
          isFullscreenMenuOpen ? 'overflow-hidden' : 'overflow-y-auto'
        }`}
        data-testid="portal-scroll-region"
      >
        <PortalMobileHeader onStarsFullscreenOpenChange={setIsStarsFullscreenOpen} />
        <IncidentBanner />
        <div
          className="portal-content-frame mx-auto w-full max-w-[1440px] px-4 py-5 sm:px-6 sm:py-7 xl:px-8 xl:py-9"
          data-testid="portal-content-frame"
        >
          <Outlet />
          {/* Optional consent follows the page, clear of the fixed navigation dock. */}
          <footer className="mt-8" aria-label="Privacy choices">
            <PortalAnalyticsConsentBanner />
          </footer>
        </div>
      </main>
      <PortalMobileNav pendingCount={pendingCount} hidden={isStarsFullscreenOpen} />
    </div>
  );
}
